import { createContext, useContext } from "react";

import {
  FORM_PAGE_DPI,
  FormSettings,
  formContentWidthPx
} from "./form-print.utils";

// ENG-2277 (HTML Forms): how a page draws its header and footer (the clinic header, the footer
// signatures). Both are laid out in px for the page every other document prints on: A4 at dpi 128 with
// 50px margins. react-pdf prints a px in the page's own units, so on a Form's page (dpi 96, the Form's
// own margins) they printed a third larger, the header wrapped onto two rows and the footer slid off
// the margins. A Form's page draws them at a scale instead.
export type PdfPageChrome = {
  /** Every px length in the header and footer is multiplied by this. */
  scale: number;
  /** The footer's distance from the page's left edge: the page's left margin. */
  footerLeft: number | string;
};

const LEGACY_PAGE_DPI = 128;
const LEGACY_MARGIN_PX = 50;

/** The width between a legacy page's margins, in its px: A4 is 595.28pt wide. */
export const LEGACY_CONTENT_WIDTH_PX =
  (595.28 / 72) * LEGACY_PAGE_DPI - 2 * LEGACY_MARGIN_PX;

export const LEGACY_PAGE_CHROME: PdfPageChrome = {
  scale: 1,
  footerLeft: LEGACY_MARGIN_PX
};

/**
 * A Form's page draws its header and footer at the size they print on any other document, and
 * smaller only when the Form's margins leave less room than a legacy page has between its own.
 */
export const formPageChrome = (settings: FormSettings): PdfPageChrome => ({
  scale: Math.min(
    FORM_PAGE_DPI / LEGACY_PAGE_DPI,
    formContentWidthPx(settings) / LEGACY_CONTENT_WIDTH_PX
  ),
  footerLeft: `${settings.margins_in.left}in`
});

export const PdfPageChromeContext =
  createContext<PdfPageChrome>(LEGACY_PAGE_CHROME);

export const usePdfPageChrome = () => useContext(PdfPageChromeContext);

/** This page's header and footer scale, and a px length at that scale. */
export const usePdfChromeScale = () => {
  const { scale } = usePdfPageChrome();
  return { scale, px: (length: number) => `${length * scale}px` };
};
